import * as React from "react";
import { useNavigate } from "react-router";
import KfzContractCreatePage from "../../pages/KfzContractCreatePage";
import ImmobilieContractCreatePage from "../../pages/ImmobilieContractCreatePage";
import { OpenWindowConfig, useWindowManager } from "./WindowManagerContext";

export type ContractFormWindowProps = {
  windowId: string;
  /** Vertragsart-Variante. */
  variant: "kfz" | "immobilie";
  /** Optional: vorausgewählter Kontakt. */
  contactId?: number | null;
  /** Optional: einzelnes Risikoobjekt (Pfad-Variante). */
  riskObjectId?: number | null;
  /** Optional: mehrere Risiken (Multi-Select / Sammelvertrag). */
  riskObjectIds?: number[];
  /** Optional: bestehender Vertrag zum Bearbeiten. */
  contractId?: number | null;
};

let contractDraftCounter = 0;

/**
 * Öffnet ein Contract-Form-Window mit aussagekräftigem Titel.
 *
 * Titel-Schema: `Neuer {KFZ|Immobilien}-(Sammel)vertrag · {Kundenname|Kontakt #ID|Entwurf #N}`.
 */
export const openContractFormWindow = (
  openWindow: (config: OpenWindowConfig) => void,
  params: {
    variant: "kfz" | "immobilie";
    contactId?: number | null;
    contactName?: string | null;
    riskObjectId?: number | null;
    riskObjectIds?: number[];
    contractId?: number | null;
    initialSize?: { width: number; height: number };
  },
): string => {
  const {
    variant,
    contactId,
    contactName,
    riskObjectId,
    riskObjectIds,
    contractId: existingContractId,
    initialSize,
  } = params;

  const variantLabel = variant === "kfz" ? "KFZ" : "Immobilien";
  const isSammel = (riskObjectIds?.length ?? 0) > 1;
  const baseLabel = isSammel
    ? `Neuer ${variantLabel}-Sammelvertrag (${riskObjectIds!.length} Risiken)`
    : existingContractId
      ? `${variantLabel}-Vertrag bearbeiten`
      : `Neuer ${variantLabel}-Vertrag`;

  const trimmedName = contactName?.trim();
  const nameSuffix = trimmedName
    ? trimmedName
    : contactId
      ? `Kontakt #${contactId}`
      : `Entwurf #${++contractDraftCounter}`;
  const title = `${baseLabel} · ${nameSuffix}`;

  const idKey = existingContractId
    ? `edit-${existingContractId}`
    : isSammel
      ? `bundle-c${contactId ?? "new"}`
      : riskObjectId
        ? `r${riskObjectId}`
        : `c${contactId ?? "new"}`;
  const windowId = `contract-${variant}-${idKey}-${Date.now()}`;

  openWindow({
    id: windowId,
    title,
    initialSize: initialSize ?? { width: 1140, height: 760 },
    // Standard: maximiert öffnen (Full-Page), wie die Kontaktanlage. Restore fällt
    // auf initialSize zurück.
    initialMaximized: true,
    render: () => (
      <ContractFormWindow
        windowId={windowId}
        variant={variant}
        contactId={contactId ?? null}
        riskObjectId={riskObjectId ?? null}
        riskObjectIds={riskObjectIds}
        contractId={existingContractId ?? null}
      />
    ),
  });
  return windowId;
};

/**
 * Rendert die existierenden Vertrags-Anlage-Pages innerhalb des Window-
 * Manager-Containers. KEIN MemoryRouter — React-Router v6 erlaubt keine
 * verschachtelten Router. Stattdessen erhalten die Pages ein embedded-
 * Prop, das URL/Router-Reads (useParams/useSearchParams) überschreibt.
 */
const ContractFormWindow: React.FC<ContractFormWindowProps> = ({
  windowId,
  variant,
  contactId,
  riskObjectId,
  riskObjectIds,
  contractId,
}) => {
  const { closeWindow } = useWindowManager();
  const navigate = useNavigate();

  const handleSuccess = React.useCallback(
    (createdId: number | null) => {
      closeWindow(windowId);
      if (createdId) navigate(`/contracts/${createdId}`);
    },
    [closeWindow, navigate, windowId],
  );

  if (variant === "kfz") {
    return (
      <KfzContractCreatePage
        embedded={{
          contactId: contactId ?? null,
          riskObjectId: riskObjectId ?? null,
          riskObjectIds: riskObjectIds ?? [],
          contractId: contractId ?? null,
          onSuccess: handleSuccess,
        }}
      />
    );
  }

  return (
    <ImmobilieContractCreatePage
      embedded={{
        contactId: contactId ?? null,
        riskObjectId: riskObjectId ?? null,
        contractId: contractId ?? null,
        onSuccess: handleSuccess,
      }}
    />
  );
};

export default ContractFormWindow;
